import { Card, CardContent, Chip, Stack, Tooltip, Typography } from "@mui/material"
import UpIcon from "@mui/icons-material/TrendingUpOutlined"
import DownIcon from "@mui/icons-material/TrendingDownOutlined"

import { INTERACTIVE_CARD } from "../motion"

/**
 * One figure, and what it was last time.
 *
 * ── Why the comparison is part of the card ──────────────────────────────────
 *
 * "32 hours" is a number. "32 hours, down 37% on the previous 30 days" is the
 * thing somebody acts on, and it is the difference between a report that is
 * read and one that is filed.
 *
 * ── Why the arrow is not coloured ───────────────────────────────────────────
 *
 * Direction is never colour alone. Up is not automatically good — up on
 * non-billable hours is bad news — so the pill states the number with an arrow
 * and stays in text ink rather than turning green for "up".
 */
export default function Figure({ label, value, caption, delta, icon: Icon, onClick }) {
  return (
    <Card sx={{ flex: 1, minWidth: 0, ...INTERACTIVE_CARD, ...(onClick ? { cursor: "pointer" } : null) }} onClick={onClick}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          {Icon ? <Icon sx={{ fontSize: 16, color: "text.disabled" }} /> : null}
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
            {label}
          </Typography>
          {delta ? (
            <Tooltip title={delta.title}>
              <Chip
                size="small"
                icon={
                  delta.direction > 0 ? (
                    <UpIcon sx={{ fontSize: "14px !important" }} />
                  ) : (
                    <DownIcon sx={{ fontSize: "14px !important" }} />
                  )
                }
                label={delta.label}
                variant="outlined"
                sx={{ height: 20, fontSize: 11, fontWeight: 650 }}
              />
            </Tooltip>
          ) : null}
        </Stack>
        <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums", my: 0.25 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </CardContent>
    </Card>
  )
}

/** A percentage change, or null when there is nothing to compare against. */
export function deltaOf(now, before, describe) {
  if (before === null || before === undefined) return null
  /* Growth from zero is not a percentage — it is "there was none before", and
     printing ∞% or 100% for it is a number somebody would quote. */
  if (!before) return now > 0 ? { direction: 1, label: "new", title: "Nothing in the previous period" } : null
  const change = Math.round(((now - before) / before) * 100)
  if (change === 0) return { direction: 1, label: "level", title: "The same as the previous period" }
  return {
    direction: Math.sign(change),
    label: `${change > 0 ? "+" : ""}${change}%`,
    title: `${describe(before)} in the previous period`,
  }
}
